import { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  Settings,
  MapPin,
  Calendar,
  Link as LinkIcon,
  MoreHorizontal,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Card, CardContent } from '@/components/ui/card'
import { FollowButton } from '@/components/ui/FollowButton'
import { EditProfileModal } from '@/components/EditProfileModal'
import { FollowersModal } from '@/components/modals/FollowersModal'
import { FollowingModal } from '@/components/modals/FollowingModal'
import { PostCard } from '@/components/PostCard'
import { usersApi, postsApi, FeedItem, User } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { formatDate, getInitials, formatNumber } from '@/lib/utils'

export function ProfilePage() {
  const { username } = useParams()
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false)
  const [isFollowersOpen, setIsFollowersOpen] = useState(false)
  const [isFollowingOpen, setIsFollowingOpen] = useState(false)

  // If no username, show current user's profile
  const profileUsername = username || currentUser?.username

  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', profileUsername],
    queryFn: () => usersApi.getProfile(profileUsername!),
    enabled: !!profileUsername,
  })

  // Handle case when no username is available (user not logged in or data not loaded)
  if (!profileUsername) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <h3 className="font-display text-lg font-semibold">Profile not available</h3>
          <p className="mt-2 text-muted-foreground">
            Please log in to view your profile
          </p>
        </CardContent>
      </Card>
    )
  }

  // API returns {user: {...}, profile: {...}} structure with snake_case fields
  interface ApiUser {
    id: string
    username: string
    display_name?: string
    is_verified?: boolean
    created_at?: string
  }
  interface ApiProfile {
    display_name?: string
    bio?: string
    avatar_url?: string
    cover_url?: string
    location?: string
    website?: string
    followers_count?: number
    following_count?: number
    friends_count?: number
    posts_count?: number
  }
  interface ApiResponse {
    user?: ApiUser
    profile?: ApiProfile | null
    is_following?: boolean
    is_friend?: boolean
  }

  const apiData = profileData?.data as ApiResponse | undefined
  const profile = apiData?.user ? {
    id: apiData.user.id,
    username: apiData.user.username,
    displayName: String(apiData.profile?.display_name || apiData.user.display_name || apiData.user.username || ''),
    createdAt: apiData.user.created_at || '',
    followersCount: apiData.profile?.followers_count ?? 0,
    followingCount: apiData.profile?.following_count ?? 0,
    friendsCount: apiData.profile?.friends_count ?? 0,
    postsCount: apiData.profile?.posts_count ?? 0,
    bio: apiData.profile?.bio,
    avatarUrl: apiData.profile?.avatar_url,
    coverUrl: apiData.profile?.cover_url,
    location: apiData.profile?.location,
    website: apiData.profile?.website,
    isVerified: apiData.user.is_verified ?? false,
    isFollowing: apiData.is_following ?? false,
    isFriend: apiData.is_friend ?? false,
  } : undefined

  const isOwnProfile = !username || username === currentUser?.username

  // Fetch user's posts
  const { data: postsData, isLoading: postsLoading } = useQuery({
    queryKey: ['userPosts', profile?.id],
    queryFn: () => postsApi.getUserPosts(profile!.id),
    enabled: !!profile?.id,
  })

  // Normalize posts data to handle both FeedItem[] and PostWithAuthor[] formats
  const normalizedPosts = useMemo(() => {
    if (!postsData?.data || !Array.isArray(postsData.data)) return []

    return (postsData.data as any[]).map((item: any): FeedItem => {
      // If item already has the FeedItem structure (item.post.post exists)
      if (item?.post?.post?.id) {
        return item as FeedItem
      }
      // If item is PostWithAuthor format (item.post.id exists directly)
      if (item?.post?.id) {
        return {
          post: item,
          user_reaction: item.liked_by_me ? 'like' : null,
        } as FeedItem
      }
      // Return as-is (may fail, but filter will catch it)
      return item
    }).filter((item: FeedItem) => item?.post?.post?.id)
  }, [postsData])

  const likeMutation = useMutation({
    mutationFn: (postId: string) => postsApi.likePost(postId),
    onMutate: async (postId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['userPosts', profile?.id] })

      // Snapshot the previous value
      const previousPosts = queryClient.getQueryData(['userPosts', profile?.id])

      // Optimistically update to the new value
      queryClient.setQueryData(['userPosts', profile?.id], (old: any) => {
        if (!old?.data) return old
        return {
          ...old,
          data: old.data.map((item: FeedItem) =>
            item.post.post.id === postId
              ? {
                  ...item,
                  post: {
                    ...item.post,
                    post: {
                      ...item.post.post,
                      reaction_count: (item.post.post.reaction_count || 0) + 1,
                    },
                    like_count: (item.post.like_count || 0) + 1,
                    liked_by_me: true,
                  },
                  user_reaction: 'like',
                }
              : item
          ),
        }
      })

      return { previousPosts }
    },
    onError: (err, postId, context) => {
      // Rollback on error
      if (context?.previousPosts) {
        queryClient.setQueryData(['userPosts', profile?.id], context.previousPosts)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['userPosts', profile?.id] })
    },
  })

  const unlikeMutation = useMutation({
    mutationFn: (postId: string) => postsApi.unlikePost(postId),
    onMutate: async (postId: string) => {
      await queryClient.cancelQueries({ queryKey: ['userPosts', profile?.id] })
      const previousPosts = queryClient.getQueryData(['userPosts', profile?.id])

      queryClient.setQueryData(['userPosts', profile?.id], (old: any) => {
        if (!old?.data) return old
        return {
          ...old,
          data: old.data.map((item: FeedItem) =>
            item.post.post.id === postId
              ? {
                  ...item,
                  post: {
                    ...item.post,
                    post: {
                      ...item.post.post,
                      reaction_count: Math.max(0, (item.post.post.reaction_count || 0) - 1),
                    },
                    like_count: Math.max(0, (item.post.like_count || 0) - 1),
                    liked_by_me: false,
                  },
                  user_reaction: null,
                }
              : item
          ),
        }
      })

      return { previousPosts }
    },
    onError: (err, postId, context) => {
      if (context?.previousPosts) {
        queryClient.setQueryData(['userPosts', profile?.id], context.previousPosts)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['userPosts', profile?.id] })
    },
  })

  const handleReact = (postId: string, isLiked: boolean) => {
    if (isLiked) {
      unlikeMutation.mutate(postId)
    } else {
      likeMutation.mutate(postId)
    }
  }

  const handleDelete = (postId: string) => {
    // Optimistically remove from posts
    queryClient.setQueryData(['userPosts', profile?.id], (old: any) => {
      if (!old?.data) return old
      return {
        ...old,
        data: old.data.filter((item: FeedItem) => item.post.post.id !== postId),
      }
    })
  }

  const handleEdit = (postId: string, content: string, mediaIds: string[]) => {
    queryClient.invalidateQueries({ queryKey: ['userPosts', profile?.id] })
  }

  if (profileLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!profile) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <h3 className="font-display text-lg font-semibold">User not found</h3>
          <p className="mt-2 text-muted-foreground">
            The user you&apos;re looking for doesn&apos;t exist
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Profile header */}
      <Card className="overflow-hidden">
        {/* Cover image */}
        <div className="relative h-32 bg-gradient-to-r from-primary/30 to-accent/30 sm:h-48">
          {profile.coverUrl && (
            <img
              src={profile.coverUrl}
              alt=""
              className="h-full w-full object-cover"
            />
          )}
        </div>

        <CardContent className="relative pb-6">
          {/* Avatar */}
          <div className="absolute -top-16 left-4 sm:-top-20 sm:left-6">
            <Avatar className="h-24 w-24 border-4 border-background sm:h-32 sm:w-32">
              <AvatarImage src={profile.avatarUrl} />
              <AvatarFallback className="text-2xl sm:text-3xl">
                {getInitials(profile.displayName)}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end pt-2">
            {isOwnProfile ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditProfileOpen(true)}
              >
                <Settings className="mr-2 h-4 w-4" />
                Edit Profile
              </Button>
            ) : (
              <div className="flex gap-2">
                <FollowButton
                  userId={profile.id}
                  username={profile.username}
                  isFollowing={profile.isFollowing}
                  size="sm"
                />
                <Button variant="outline" size="sm">
                  Message
                </Button>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Profile info */}
          <div className="mt-8 sm:mt-4">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-xl font-bold sm:text-2xl">
                {profile.displayName}
              </h1>
              {profile.isVerified && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                  ✓
                </span>
              )}
            </div>
            <p className="text-muted-foreground">@{profile.username}</p>

            {profile.bio && (
              <p className="mt-3 whitespace-pre-wrap">{profile.bio}</p>
            )}

            <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
              {profile.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {profile.location}
                </span>
              )}
              {profile.website && (
                <a
                  href={profile.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-primary hover:underline"
                >
                  <LinkIcon className="h-4 w-4" />
                  {profile.website.replace(/^https?:\/\/(www\.)?/, '')}
                </a>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Joined {formatDate(profile.createdAt)}
              </span>
            </div>

            {/* Stats */}
            <div className="mt-4 flex gap-4 text-sm">
              <button
                className="hover:underline"
                onClick={() => setIsFollowingOpen(true)}
              >
                <span className="font-semibold">{formatNumber(profile.followingCount)}</span>{' '}
                <span className="text-muted-foreground">Following</span>
              </button>
              <button
                className="hover:underline"
                onClick={() => setIsFollowersOpen(true)}
              >
                <span className="font-semibold">{formatNumber(profile.followersCount)}</span>{' '}
                <span className="text-muted-foreground">Followers</span>
              </button>
              <button className="hover:underline">
                <span className="font-semibold">{formatNumber(profile.friendsCount)}</span>{' '}
                <span className="text-muted-foreground">Friends</span>
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Posts section */}
      <div className="space-y-4">
        <h2 className="font-display text-xl font-bold">Posts</h2>

        {postsLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : normalizedPosts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                {isOwnProfile
                  ? "You haven't posted anything yet"
                  : `${profile.displayName} hasn't posted anything yet`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {normalizedPosts.map((item: FeedItem, index: number) => (
              <motion.div
                key={item.post.post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <PostCard
                  item={item}
                  onReact={handleReact}
                  currentUser={currentUser}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        open={isEditProfileOpen}
        onOpenChange={setIsEditProfileOpen}
      />

      {/* Followers Modal */}
      {profile && (
        <FollowersModal
          open={isFollowersOpen}
          onOpenChange={setIsFollowersOpen}
          userId={profile.id}
          username={profile.username}
          initialCount={profile.followersCount}
        />
      )}

      {/* Following Modal */}
      {profile && (
        <FollowingModal
          open={isFollowingOpen}
          onOpenChange={setIsFollowingOpen}
          userId={profile.id}
          username={profile.username}
          initialCount={profile.followingCount}
        />
      )}
    </div>
  )
}
